/**
 * Hook to fetch taste weights for the ranking pipeline.
 * Cached per session — taste weights change slowly (new interactions
 * only shift scores marginally), so we fetch once and reuse.
 */

import { useState, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';
import type { TasteWeights } from '@/utils/applyTasteRerank';

export function useTasteWeights(userId: string | null) {
  const [weights, setWeights] = useState<TasteWeights | null>(null);
  const [loading, setLoading] = useState(false);
  const fetchedForRef = useRef<string | null>(null);

  useEffect(() => {
    if (!userId || !supabase) {
      setWeights(null);
      fetchedForRef.current = null;
      return;
    }

    // Already fetched for this user this session
    if (fetchedForRef.current === userId) return;

    let cancelled = false;
    setLoading(true);

    supabase.rpc('get_taste_weights', { p_user_id: userId })
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error || !data) {
          console.warn('Failed to fetch taste weights:', error?.message);
          setWeights(null);
        } else {
          setWeights({
            typeWeights: data.type_weights || {},
            likeCount: data.like_count || 0,
            dislikeCount: data.dislike_count || 0,
            tasteHint: {
              liked: data.taste_hint?.liked || [],
              disliked: data.taste_hint?.disliked || [],
            },
          });
          fetchedForRef.current = userId;
        }
        setLoading(false);
      });

    return () => { cancelled = true; };
  }, [userId]);

  /** Force a refresh (e.g. after a burst of new interactions) */
  const refresh = () => {
    fetchedForRef.current = null;
    if (userId) {
      // Trigger re-fetch by resetting the ref
      setLoading(true);
      supabase?.rpc('get_taste_weights', { p_user_id: userId })
        .then(({ data, error }) => {
          if (error || !data) {
            setWeights(null);
          } else {
            setWeights({
              typeWeights: data.type_weights || {},
              likeCount: data.like_count || 0,
              dislikeCount: data.dislike_count || 0,
              tasteHint: {
                liked: data.taste_hint?.liked || [],
                disliked: data.taste_hint?.disliked || [],
              },
            });
            fetchedForRef.current = userId;
          }
          setLoading(false);
        });
    }
  };

  return { weights, loading, refresh };
}
